import { Link } from '@tanstack/react-router';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import Icon from '../ui/Icon';
import type { Folder } from '../../types/api';
import type { DropTargetData } from '../../contexts/BookmarkDndContext';
import { formatCount } from '../../utils/numberUtils';
import { useContextMenu } from '../../hooks/useContextMenu';
import SidebarItemMenu from './SidebarItemMenu';
import clsx from 'clsx';

export type DropIndicatorPosition = 'above' | 'below' | null;

function DropIndicator({ position }: { position: DropIndicatorPosition }) {
  if (!position) return null;
  return (
    <div
      className={clsx(
        'sort-indicator',
        position === 'above' ? '-top-[2.5px]' : '-bottom-[2.5px]',
      )}
    />
  );
}

export interface SortableFolderItemProps {
  folder: Folder;
  isSelected: boolean;
  /** True when the Folders section is collapsed — droppables stay mounted but must not accept drops. */
  dropDisabled?: boolean;
  dropIndicatorPosition?: DropIndicatorPosition;
  /** Triggers entrance animation */
  justMoved?: boolean;
  /** Prevents snap-back of displaced items */
  suppressTransition?: boolean;
}

function SortableFolderItem({
  folder,
  isSelected,
  dropDisabled = false,
  dropIndicatorPosition,
  justMoved,
  suppressTransition,
}: SortableFolderItemProps) {
  const { id, slug } = folder;
  const contextMenu = useContextMenu();

  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: folder.id,
    data: {
      type: 'folder-sort',
      folder,
    },
  });

  const isDropTarget = !dropDisabled && !isSelected;

  const dropData: DropTargetData = { type: 'folder', folderId: folder.id };
  const { setNodeRef: setDroppableRef, isOver: isBookmarkOver } = useDroppable({
    id: `folder-drop-${folder.id}`,
    data: dropData,
    disabled: dropDisabled || isSelected,
  });

  const style = (() => {
    if (isDragging) return { opacity: 0 };
    if (suppressTransition) return { transform: undefined, transition: 'none' };
    if (justMoved)
      return { transform: 'scale(0.95)', transition: 'transform 150ms ease-out' };
    return { transform: CSS.Transform.toString(transform) || undefined, transition };
  })();

  const setNodeRef = (node: HTMLLIElement | null) => {
    setSortableRef(node);
    setDroppableRef(node);
  };

  // dnd-kit's default sortable attributes include role="button",
  // aria-pressed, and aria-roledescription="sortable" — all of which are
  // invalid on an <li> that contains a focusable <Link>, and which together
  // produced three axe violations on the sidebar (nested-interactive, list,
  // aria-allowed-attr). Strip them so the row stays a real list item;
  // the kept aria-describedby still points keyboard/SR users at dnd-kit's
  // sortable instructions. Mouse drag continues to work via listeners; we
  // already override tabIndex to -1 below, so dnd-kit's keyboard mode was
  // already off at the row level.
  const {
    role: _ignoredRole,
    tabIndex: _ignoredTabIndex,
    'aria-pressed': _ignoredAriaPressed,
    'aria-roledescription': _ignoredAriaRoleDescription,
    ...sortableAttrs
  } = attributes;

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...sortableAttrs}
      {...listeners}
      tabIndex={-1}
      id={`folder_${id}`}
      data-folder-id={id}
      onContextMenu={contextMenu.handleContextMenu}
    >
      <DropIndicator position={dropIndicatorPosition ?? null} />

      <Link
        to="/folder/$folderId/$slug"
        params={{ folderId: id, slug }}
        className={clsx('side-item folder-link', {
          'pointer-events-none': isDragging,
          'drop-target-highlight': isBookmarkOver && isDropTarget,
        })}
        data-active={isSelected ? 'true' : undefined}
        data-droptarget={isDropTarget ? 'true' : undefined}
        onClick={(e) => isDragging && e.preventDefault()}
      >
        <Icon name={folder.public ? 'folder-public' : 'folder'} />
        <span className="folder-name max-w-[138px] truncate leading-snug">
          {folder.displayTitle}
        </span>
        {folder.count > 0 && (
          <span className="side-nav-count" title={String(folder.count)}>
            {formatCount(folder.count)}
          </span>
        )}
      </Link>

      {isSelected && (
        <a
          className="folder-edit-icon hidden"
          data-modal="folder-edit-modal"
          data-folder-id={folder.id}
          data-folder-action={`/folder/edit/${folder.id}`}
          data-folder-title={folder.displayTitle}
          data-folder-sync-to-iphone={folder.syncToMobile ? 'true' : 'false'}
          data-folder-public={folder.public ? 'true' : 'false'}
          href="#"
        >
          <i className="icon icon-pencil"></i>
        </a>
      )}

      {contextMenu.anchorPosition && (
        <span className="absolute" aria-hidden>
          <SidebarItemMenu
            folder={folder}
            isActive={isSelected}
            anchorPosition={contextMenu.anchorPosition}
            onOpenChange={contextMenu.handleOpenChange}
          />
        </span>
      )}
    </li>
  );
}

export default SortableFolderItem;
